/* Demo Experience — cinematic intro + persistent signature soundtrack.
   Mounted once at App level. Renders NOTHING unless Demo Mode is ON. */
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Play, Pause, Volume2, VolumeX, X, Music } from "lucide-react";

const F1_IMG = "https://customer-assets-39nsmqrw.emergentagent.net/job_accident-hub/artifacts/t06ld0gu_C946F68C-3417-4618-86EC-F49B2BC59A3D.png";

const LOAD_MSGS = [
  "Initializing Enterprise Infrastructure…", "Loading AI Systems…", "Connecting Legal Intelligence…",
  "Initializing Medical Network…", "Connecting Drone Command…", "Preparing Attorney Marketplace…",
  "Loading Secure Evidence Vault…", "Verifying Enterprise Security…", "Connecting Telehealth Network…",
  "Preparing Premium Experience…", "Finalizing Demo Environment…",
];
const HUD = ["AI ONLINE", "SECURE", "MEDICAL NETWORK", "ATTORNEY MARKETPLACE", "DRONE COMMAND", "RN NETWORK", "ENTERPRISE", "REAL-TIME", "ENCRYPTED", "CONNECTED"];
const BRAND = ["The Future of Accident Response", "Enterprise Legal Intelligence", "AI-Powered Case Acquisition", "Medical + Legal + Drone Ecosystem", "Innovating Today. Transforming Tomorrow."];

/* ---------------- procedural placeholder soundtrack (original, ~92 BPM) ---------------- */
function createSynth(ctx, master) {
  const BPM = 92, beat = 60 / BPM, bar = beat * 4;
  // Am7 – Fmaj7 – Cmaj7 – G  (roots A2, F2, C3, G2)
  const CHORDS = [
    { root: 55.0, notes: [220.0, 261.63, 329.63, 392.0] },
    { root: 43.65, notes: [174.61, 220.0, 261.63, 329.63] },
    { root: 65.41, notes: [261.63, 329.63, 392.0, 493.88] },
    { root: 49.0, notes: [196.0, 246.94, 293.66, 392.0] },
  ];
  let running = false, nextBar = 0, barIdx = 0, timer = null;
  const padFilter = ctx.createBiquadFilter();
  padFilter.type = "lowpass"; padFilter.frequency.value = 1100; padFilter.Q.value = 0.4;
  padFilter.connect(master);

  const scheduleBar = (t, chord) => {
    // warm pad
    chord.notes.forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = i % 2 ? "triangle" : "sawtooth"; o.frequency.value = f; o.detune.value = (i - 1.5) * 6;
      g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.028, t + 0.6);
      g.gain.setValueAtTime(0.028, t + bar - 0.5); g.gain.linearRampToValueAtTime(0, t + bar);
      o.connect(g); g.connect(padFilter); o.start(t); o.stop(t + bar + 0.1);
    });
    // deep controlled sub bass: 1 . . 3.5 pattern
    [[0, 0.9], [1.5, 0.4], [2, 0.9], [3.5, 0.45]].forEach(([b, len]) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; o.frequency.value = chord.root;
      const s = t + b * beat;
      g.gain.setValueAtTime(0, s); g.gain.linearRampToValueAtTime(0.16, s + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, s + len * beat);
      o.connect(g); g.connect(master); o.start(s); o.stop(s + len * beat + 0.05);
    });
    // soft kick on 1 & 3
    [0, 2].forEach((b) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sine"; const s = t + b * beat;
      o.frequency.setValueAtTime(120, s); o.frequency.exponentialRampToValueAtTime(40, s + 0.12);
      g.gain.setValueAtTime(0.22, s); g.gain.exponentialRampToValueAtTime(0.001, s + 0.16);
      o.connect(g); g.connect(master); o.start(s); o.stop(s + 0.2);
    });
    // crisp hats: 8ths with off-beat accents
    for (let i = 0; i < 8; i++) {
      const s = t + i * beat * 0.5;
      const buf = ctx.createBuffer(1, ctx.sampleRate * 0.05, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * Math.exp(-j / (d.length / 6));
      const src = ctx.createBufferSource(); src.buffer = buf;
      const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 8000;
      const g = ctx.createGain(); g.gain.value = i % 2 ? 0.05 : 0.028;
      src.connect(hp); hp.connect(g); g.connect(master); src.start(s);
    }
  };
  const tick = () => {
    if (!running) return;
    while (nextBar < ctx.currentTime + 0.4) {
      scheduleBar(nextBar, CHORDS[barIdx % CHORDS.length]);
      barIdx += 1; nextBar += bar;
    }
    timer = setTimeout(tick, 120);
  };
  return {
    start() { if (running) return; running = true; nextBar = ctx.currentTime + 0.05; barIdx = 0; tick(); },
    stop() { running = false; if (timer) clearTimeout(timer); },
  };
}

function playEngineSound(ctx, master) {
  const t = ctx.currentTime;
  const g = ctx.createGain(); g.connect(master);
  const o = ctx.createOscillator(); o.type = "sawtooth"; o.connect(g);
  // electrical power-on hum → rev → throttle blip → idle
  o.frequency.setValueAtTime(50, t);
  g.gain.setValueAtTime(0.001, t); g.gain.linearRampToValueAtTime(0.05, t + 0.4);
  o.frequency.linearRampToValueAtTime(90, t + 0.8);
  o.frequency.exponentialRampToValueAtTime(520, t + 1.5);          // rev to life
  g.gain.linearRampToValueAtTime(0.14, t + 1.5);
  o.frequency.exponentialRampToValueAtTime(180, t + 2.1);
  o.frequency.exponentialRampToValueAtTime(760, t + 2.5);          // aggressive blip
  g.gain.linearRampToValueAtTime(0.17, t + 2.5);
  o.frequency.exponentialRampToValueAtTime(110, t + 3.3);          // settle to idle
  g.gain.linearRampToValueAtTime(0.06, t + 3.4);
  g.gain.linearRampToValueAtTime(0.0, t + 5.0);
  o.start(t); o.stop(t + 5.1);
  // engine texture noise
  const buf = ctx.createBuffer(1, ctx.sampleRate * 5, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let j = 0; j < d.length; j++) d[j] = (Math.random() * 2 - 1) * 0.4;
  const src = ctx.createBufferSource(); src.buffer = buf;
  const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.setValueAtTime(300, t);
  bp.frequency.exponentialRampToValueAtTime(1400, t + 2.5); bp.frequency.exponentialRampToValueAtTime(400, t + 4);
  const ng = ctx.createGain(); ng.gain.setValueAtTime(0.0, t); ng.gain.linearRampToValueAtTime(0.05, t + 1.2);
  ng.gain.linearRampToValueAtTime(0.0, t + 5);
  src.connect(bp); bp.connect(ng); ng.connect(master); src.start(t); src.stop(t + 5);
}

/* ---------------- cinematic intro ---------------- */
const DemoIntro = ({ intro, onDone, audioCtxRef }) => {
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const [brandIdx, setBrandIdx] = useState(0);
  const [exiting, setExiting] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const dur = (intro?.duration_s || 5) * 1000;

  const enableSound = useCallback(() => {
    try {
      const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      ctx.resume().then(() => { playEngineSound(ctx, ctx.destination); setSoundOn(true); });
    } catch { /* audio unsupported */ }
  }, [audioCtxRef]);

  useEffect(() => {
    enableSound(); // attempt autoplay; browsers may require a gesture
    const t0 = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(100, ((Date.now() - t0) / dur) * 100);
      setProgress(p);
      if (p >= 100) { clearInterval(iv); setExiting(true); setTimeout(onDone, 900); }
    }, 50);
    const mi = setInterval(() => setMsgIdx((i) => (i + 1) % LOAD_MSGS.length), 460);
    const bi = setInterval(() => setBrandIdx((i) => (i + 1) % BRAND.length), 1300);
    return () => { clearInterval(iv); clearInterval(mi); clearInterval(bi); };
  }, [dur, onDone, enableSound]);

  return (
    <div className={`fixed inset-0 z-[200] bg-black overflow-hidden transition-opacity duration-700 ${exiting ? "opacity-0" : "opacity-100"}`} data-testid="demo-intro">
      <img src={F1_IMG} alt="" className="absolute inset-0 w-full h-full object-cover"
        style={{ animation: `crIntroZoom ${(dur + 1200) / 1000}s ease-in-out forwards`, transformOrigin: "52% 55%" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/60" />
      {/* scan lines + particles */}
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(59,130,246,0.08) 4px)" }} />
      {[...Array(14)].map((_, i) => (
        <div key={i} className="absolute w-1 h-1 rounded-full bg-blue-400/60" style={{
          left: `${(i * 7.3) % 100}%`, top: `${(i * 13.7) % 100}%`,
          animation: `crFloat ${3 + (i % 4)}s ease-in-out ${i * 0.3}s infinite alternate` }} />
      ))}
      {/* HUD chips */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-3">
        {HUD.slice(0, 5).map((h, i) => (
          <div key={h} className="px-3 py-1.5 rounded border border-blue-500/30 bg-blue-950/40 backdrop-blur-sm text-blue-300 text-[10px] font-mono tracking-widest"
            style={{ animation: `crHud 2.4s ease-in-out ${i * 0.4}s infinite alternate` }}>{h}</div>
        ))}
      </div>
      <div className="absolute right-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-3 items-end">
        {HUD.slice(5).map((h, i) => (
          <div key={h} className="px-3 py-1.5 rounded border border-blue-500/30 bg-blue-950/40 backdrop-blur-sm text-blue-300 text-[10px] font-mono tracking-widest"
            style={{ animation: `crHud 2.4s ease-in-out ${i * 0.4 + 0.2}s infinite alternate` }}>{h}</div>
        ))}
      </div>
      {/* center content */}
      <div className="absolute inset-x-0 top-[8%] text-center px-4">
        <div className="font-mono text-[10px] tracking-[0.5em] text-blue-400 mb-3">CRASH READY · DEMO ENVIRONMENT · SECURE SESSION</div>
        <h1 className="font-heading text-3xl sm:text-5xl font-extrabold text-white tracking-wide" data-testid="demo-intro-headline">{intro?.headline || "DEMO MODE INITIATING"}</h1>
        <p className="text-blue-200/80 text-sm sm:text-base mt-2">{intro?.subline || "Loading the Future of Legal Technology…"}</p>
      </div>
      <div className="absolute inset-x-0 bottom-[10%] flex flex-col items-center px-6">
        <div className="text-blue-300 font-mono text-xs h-5 mb-3" data-testid="demo-intro-message">{LOAD_MSGS[msgIdx]}</div>
        <div className="w-full max-w-md h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-blue-600 to-cyan-400 transition-[width] duration-100" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-2 font-mono text-[11px] text-white/70">{progress >= 100 ? "100% — Demo Ready" : `${Math.round(progress)}%`}</div>
        <div className="mt-4 text-white/50 text-xs italic transition-opacity duration-500">{BRAND[brandIdx]}</div>
      </div>
      <div className="absolute bottom-4 right-4 flex gap-2">
        {!soundOn && (
          <button onClick={enableSound} data-testid="demo-intro-sound" className="px-3 py-1.5 rounded-full border border-white/20 text-white/60 text-[11px] hover:text-white">🔊 Enable sound</button>
        )}
        <button onClick={() => { setExiting(true); setTimeout(onDone, 300); }} data-testid="demo-intro-skip"
          className="px-3 py-1.5 rounded-full border border-white/20 text-white/60 text-[11px] hover:text-white">Skip intro →</button>
      </div>
      <style>{`
        @keyframes crIntroZoom { 0% { transform: scale(1.12) translateX(1.5%); } 85% { transform: scale(1.02) translateX(-0.5%); } 100% { transform: scale(1.3); opacity: 0.85; } }
        @keyframes crHud { from { opacity: 0.35; } to { opacity: 1; } }
        @keyframes crFloat { from { transform: translateY(0); opacity: .3; } to { transform: translateY(-14px); opacity: .8; } }
      `}</style>
    </div>
  );
};

/* ---------------- persistent music player ---------------- */
const MusicPlayer = ({ music, audioCtxRef }) => {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [closed, setClosed] = useState(false);
  const [vol, setVol] = useState(music.volume ?? 0.5);
  const gainRef = useRef(null);
  const synthRef = useRef(null);
  const audioElRef = useRef(null);
  const duckedRef = useRef(false);

  const ensureCtx = useCallback(() => {
    const ctx = audioCtxRef.current || new (window.AudioContext || window.webkitAudioContext)();
    audioCtxRef.current = ctx;
    return ctx;
  }, [audioCtxRef]);

  const setGain = useCallback((v, ramp = 0.3) => {
    const g = gainRef.current;
    if (g) {
      const ctx = audioCtxRef.current;
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
      g.gain.linearRampToValueAtTime(v, ctx.currentTime + ramp);
    }
    if (audioElRef.current) audioElRef.current.volume = Math.min(1, Math.max(0, v));
  }, [audioCtxRef]);

  const start = useCallback(async () => {
    try {
      const ctx = ensureCtx();
      await ctx.resume();
      if (music.has_file) {
        if (!audioElRef.current) {
          const res = await api.get("/demo-mode/music/stream", { responseType: "blob" });
          const el = new Audio(URL.createObjectURL(res.data));
          el.loop = music.loop !== false;
          audioElRef.current = el;
        }
        audioElRef.current.volume = 0;
        await audioElRef.current.play();
        // manual fade for the element
        const target = muted ? 0 : vol, steps = 20, dur = (music.fade_in_s ?? 2.5) * 1000;
        for (let i = 1; i <= steps; i++) setTimeout(() => { if (audioElRef.current) audioElRef.current.volume = (target * i) / steps; }, (dur / steps) * i);
      } else {
        if (!gainRef.current) { gainRef.current = ctx.createGain(); gainRef.current.connect(ctx.destination); }
        gainRef.current.gain.value = 0;
        if (!synthRef.current) synthRef.current = createSynth(ctx, gainRef.current);
        synthRef.current.start();
        setGain(muted ? 0 : vol, music.fade_in_s ?? 2.5);
      }
      setPlaying(true);
    } catch { setPlaying(false); /* autoplay blocked — user presses play */ }
  }, [ensureCtx, music, vol, muted, setGain]);

  const stop = useCallback((fade = true) => {
    const dur = fade ? (music.fade_out_s ?? 2) : 0.05;
    setGain(0, dur);
    setTimeout(() => {
      synthRef.current?.stop();
      if (audioElRef.current) audioElRef.current.pause();
    }, dur * 1000 + 100);
    setPlaying(false);
  }, [music, setGain]);

  useEffect(() => { start(); return () => stop(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // admin preview/stop controls
  useEffect(() => {
    const onPrev = () => start(); const onStop = () => stop();
    window.addEventListener("cr-demo-music-preview", onPrev);
    window.addEventListener("cr-demo-music-stop", onStop);
    return () => { window.removeEventListener("cr-demo-music-preview", onPrev); window.removeEventListener("cr-demo-music-stop", onStop); };
  }, [start, stop]);

  // smart ducking: any other audio/video playback drops music to ~5%
  useEffect(() => {
    const isOurs = (t) => t === audioElRef.current;
    const onPlay = (e) => { if ((e.target.tagName === "AUDIO" || e.target.tagName === "VIDEO") && !isOurs(e.target)) { duckedRef.current = true; setGain(muted ? 0 : vol * 0.05, 0.4); } };
    const onStop2 = (e) => { if ((e.target.tagName === "AUDIO" || e.target.tagName === "VIDEO") && !isOurs(e.target) && duckedRef.current) { duckedRef.current = false; setGain(muted ? 0 : vol, 0.8); } };
    document.addEventListener("play", onPlay, true);
    document.addEventListener("pause", onStop2, true);
    document.addEventListener("ended", onStop2, true);
    return () => { document.removeEventListener("play", onPlay, true); document.removeEventListener("pause", onStop2, true); document.removeEventListener("ended", onStop2, true); };
  }, [vol, muted, setGain]);

  useEffect(() => { if (playing) setGain(muted ? 0 : vol, 0.2); }, [vol, muted, playing, setGain]);

  if (closed) return null;
  return (
    <div className="fixed bottom-4 right-4 z-[90] flex items-center gap-2 rounded-full bg-navy/90 backdrop-blur-md border border-white/10 pl-3 pr-2 py-2 shadow-2xl" data-testid="demo-music-player">
      <Music className="w-4 h-4 text-cobalt shrink-0" />
      <span className="text-[11px] text-white/80 font-medium max-w-[130px] truncate hidden sm:block">{music.track_title || "Crash Ready Theme"}</span>
      <button onClick={() => (playing ? stop() : start())} data-testid="demo-player-toggle" className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white">
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
      </button>
      <button onClick={() => setMuted(!muted)} data-testid="demo-player-mute" className="p-1.5 rounded-full hover:bg-white/10 text-white/70">
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
      </button>
      <input type="range" min="0" max="1" step="0.05" value={vol} onChange={(e) => setVol(Number(e.target.value))}
        data-testid="demo-player-volume" className="w-16 accent-cobalt hidden sm:block" />
      <button onClick={() => { stop(); setClosed(true); }} data-testid="demo-player-close" className="p-1.5 rounded-full hover:bg-white/10 text-white/50"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
};

/* ---------------- root ---------------- */
export default function DemoExperience() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [introDone, setIntroDone] = useState(() => sessionStorage.getItem("cr_demo_intro_shown") === "1");
  const audioCtxRef = useRef(null);

  const load = useCallback(() => {
    if (!user) { setCfg(null); return; }
    api.get("/settings/demo-mode").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, [user]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("cr-demo-refresh", h);
    return () => window.removeEventListener("cr-demo-refresh", h);
  }, [load]);

  if (!user || !cfg?.enabled) return null;
  const scopeOk = cfg.music?.session_scope === "all_users" || localStorage.getItem("cr_demo_music_device") === "1";
  const showIntro = cfg.intro?.enabled && !introDone;
  return (
    <>
      {showIntro && <DemoIntro intro={cfg.intro} audioCtxRef={audioCtxRef}
        onDone={() => { sessionStorage.setItem("cr_demo_intro_shown", "1"); setIntroDone(true); }} />}
      {cfg.music?.enabled && scopeOk && !showIntro && <MusicPlayer music={cfg.music} audioCtxRef={audioCtxRef} />}
    </>
  );
}
